import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  Box,
  Stack,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import RichTextEditor from "components/richTextEditor/richTextEditor";

const steps = [
  "Basic Info",
  "Recurrence",
  "Date & Time",
  "Visibility",
  "Message",
];

const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const initialFormState = {
  title: "",
  eventType: "",
  message: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  allDay: true,
  recurrence: false,
  frequency: "",
  daysOfWeek: [],
  recurrenceType: "", // "infinite" | "finite"
  recurrenceEndDate: "", // required only when recurrenceType === "finite"
  visibility: "",
  participants: [],
  academicYear: [],
  sections: [],
};

const theme = {
  primary: "rgb(35, 71, 159)",
  primaryLight: "rgba(35, 71, 159, 0.08)",
  radius: "10px",
};

const sectionLabel = {
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.07em",
  color: "#888",
  textTransform: "uppercase",
  marginBottom: "12px",
};

const toggleBox = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 14px",
  background: "rgba(0,0,0,0.03)",
  borderRadius: "10px",
  border: "0.5px solid rgba(0,0,0,0.08)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.radius,
    fontSize: "13px",
    "&.Mui-focused fieldset": { borderColor: theme.primary },
  },
  "& label.Mui-focused": { color: theme.primary },
};

const getTodayStr = () => new Date().toISOString().split("T")[0];

const MultiSelect = ({ label, name, value, onChange, options, error }) => {
  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

  return (
    <FormControl size="small" fullWidth error={!!error} sx={fieldSx}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        name={name}
        value={value}
        onChange={onChange}
        label={label}
        sx={{ borderRadius: theme.radius, fontSize: "13px" }}
        renderValue={(selected) =>
          selected
            .map((val) => {
              const found = options.find((opt) => opt.value === val);
              return capitalize(found?.label || val);
            })
            .join(", ")
        }
      >
        {options?.length > 0 ? (
          options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={value.includes(opt.value)} />
              <Box sx={{ textTransform: "capitalize" }}>
                {capitalize(opt.label)}
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No options found</MenuItem>
        )}
      </Select>
      <FormHelperText>{error}</FormHelperText>
    </FormControl>
  );
};

const EventsForm = ({
  open,
  onClose,
  onSubmit,
  initialData,
  classSections,
  users,
  academicYears,
  eventTypes,
  formLoading,
  setInitialData,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      const hasRecurrence =
        initialData.recurrence?.frequency &&
        initialData.recurrence.frequency !== "none";

      const recurrenceEndDate = initialData.recurrence?.endDate
        ? initialData.recurrence.endDate.split("T")[0]
        : "";
      const recurrenceType = hasRecurrence
        ? recurrenceEndDate
          ? "finite"
          : "infinite"
        : "";

      setForm({
        ...initialFormState,
        title: initialData.title || "",
        eventType: initialData.eventType || "",
        message: initialData.message || "",
        startDate: initialData.startDate
          ? initialData.startDate.split("T")[0]
          : "",
        endDate: initialData.endDate ? initialData.endDate.split("T")[0] : "",
        startTime: initialData.startTime || "",
        endTime: initialData.endTime || "",
        allDay: !!initialData.allDay,
        recurrence: hasRecurrence,
        frequency:
          initialData.recurrence?.frequency === "none"
            ? ""
            : initialData.recurrence?.frequency || "",
        daysOfWeek: initialData.recurrence?.daysOfWeek || [],
        recurrenceType,
        recurrenceEndDate,
        visibility: initialData.visibility || "",
        participants: initialData.participants?.map((p) => p.value) || [],
        academicYear: initialData.academicYears?.map((a) => a.value) || [],
        sections: initialData.classSections?.map((c) => c.value) || [],
      });
    } else {
      setForm(initialFormState);
    }
    setActiveStep(0);
  }, [initialData, open]);

  const validateStep = (step, data) => {
    let temp = {};

    if (step === 0) {
      if (!data.title) temp.title = "Required";
      if (!data.eventType) temp.eventType = "Required";
    }

    if (step === 1 && data.recurrence) {
      if (!data.frequency) temp.frequency = "Required";
      if (data.frequency === "weekly" && data.daysOfWeek.length === 0)
        temp.daysOfWeek = "Required";

      // recurrenceType is required when recurrence is enabled
      if (!data.recurrenceType) temp.recurrenceType = "Required";

      // recurrenceEndDate is required only when finite
      if (data.recurrenceType === "finite") {
        if (!data.recurrenceEndDate) {
          temp.recurrenceEndDate = "Required";
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const recEnd = new Date(data.recurrenceEndDate);
          recEnd.setHours(0, 0, 0, 0);
          if (recEnd < today) {
            temp.recurrenceEndDate = "End date cannot be in the past";
          }
        }
      }
    }

    if (step === 2) {
      const now = new Date();

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const startDate = data.startDate ? new Date(data.startDate) : null;
      const endDate = data.endDate ? new Date(data.endDate) : null;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(0, 0, 0, 0);

      if (!data.startDate) {
        temp.startDate = "Required";
      } else if (startDate < today) {
        temp.startDate = "Start date cannot be in the past";
      }

      if (!data.recurrence && !data.allDay) {
        if (!data.endDate) {
          temp.endDate = "Required";
        } else if (endDate < today) {
          temp.endDate = "End date cannot be in the past";
        } else if (endDate < startDate) {
          temp.endDate = "End date cannot be before start date";
        }
      }

      if (!data.allDay) {
        if (!data.startTime) temp.startTime = "Required";

        if (!data.endTime) {
          temp.endTime = "Required";
        }

        if (
          data.startTime &&
          data.startDate &&
          startDate.getTime() === today.getTime()
        ) {
          const [h, m] = data.startTime.split(":");
          const startDateTime = new Date();
          startDateTime.setHours(h, m, 0, 0);

          if (startDateTime < now) {
            temp.startTime = "Start time cannot be in the past";
          }
        }

        if (
          data.endTime &&
          data.endDate &&
          endDate.getTime() === today.getTime()
        ) {
          const [h, m] = data.endTime.split(":");
          const endDateTime = new Date();
          endDateTime.setHours(h, m, 0, 0);

          if (endDateTime < now) {
            temp.endTime = "End time cannot be in the past";
          }
        }

        if (
          data.startTime &&
          data.endTime &&
          data.startDate === data.endDate &&
          data.endTime <= data.startTime
        ) {
          temp.endTime = "End time must be after start time";
        }
      }
    }

    if (step === 3) {
      if (!data.visibility) temp.visibility = "Required";
      if (data.visibility === "user" && data.participants.length === 0)
        temp.participants = "Required";
      if (data.visibility === "academicYear" && data.academicYear.length === 0)
        temp.academicYear = "Required";
      if (data.visibility === "classSection" && data.sections.length === 0)
        temp.sections = "Required";
    }

    if (step === 4) {
      if (!data.message) temp.message = "Required";
    }

    return temp;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let updatedForm = {
      ...form,
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "visibility") {
      updatedForm = {
        ...updatedForm,
        participants: [],
        academicYear: [],
        sections: [],
      };
    }

    if (name === "allDay" && checked) {
      updatedForm = {
        ...updatedForm,
        endDate: "",
        startTime: "",
        endTime: "",
      };
    }

    if (name === "recurrence" && checked) {
      updatedForm = {
        ...updatedForm,
        endDate: "",
      };
    }

    if (name === "recurrence" && !checked) {
      updatedForm = {
        ...updatedForm,
        frequency: "",
        daysOfWeek: [],
        recurrenceType: "",
        recurrenceEndDate: "",
      };
    }

    // Clear end date when switching back to infinite
    if (name === "recurrenceType" && value === "infinite") {
      updatedForm = {
        ...updatedForm,
        recurrenceEndDate: "",
      };
    }

    setForm(updatedForm);
    setErrors(validateStep(activeStep, updatedForm));
  };

  const handleNext = () => {
    const stepErrors = validateStep(activeStep, form);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) setActiveStep((p) => p + 1);
  };

  const handleBack = () => {
    setErrors({});
    setActiveStep((p) => p - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalErrors = {
      ...validateStep(0, form),
      ...validateStep(1, form),
      ...validateStep(2, form),
      ...validateStep(3, form),
      ...validateStep(4, form),
    };
    setErrors(finalErrors);
    if (Object.keys(finalErrors).length !== 0) return;

    const payload = {
      title: form.title,
      message: form.message,
      startDate: form.startDate,
      allDay: form.allDay,
      visibility: form.visibility,
      eventType: form.eventType,
    };

    if (!form.recurrence && !form.allDay) {
      payload.endDate = form.endDate;
    }

    if (!form.allDay) {
      payload.startTime = form.startTime;
      payload.endTime = form.endTime;
    }

    if (form.recurrence) {
      payload.frequency = form.frequency;
      if (form.frequency === "weekly") {
        payload.daysOfWeek = form.daysOfWeek;
      }
      // null = infinite; date string = ends on that date
      payload.recurrenceEndDate =
        form.recurrenceType === "finite" ? form.recurrenceEndDate : null;
    }

    if (form.visibility === "user") payload.participants = form.participants;
    if (form.visibility === "academicYear")
      payload.academicYear = form.academicYear;
    if (form.visibility === "classSection")
      payload.classSection = form.sections;

    onSubmit(payload);
  };

  const handleCancel = () => {
    setInitialData(null);
    setForm(initialFormState);
    setErrors({});
    setActiveStep(0);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: 600,
          fontSize: "20px",
          pb: 0,
          pt: 2.5,
          color: "text.primary",
        }}
      >
        {initialData ? "Edit Event" : "Create Event"}
      </DialogTitle>

      <Box
        sx={{ px: 3, pt: 2, pb: 0, borderBottom: "1px solid rgba(0,0,0,0.07)" }}
      >
        <Stepper
          activeStep={activeStep}
          sx={{
            pb: 2,
            "& .MuiStepLabel-label": { fontSize: "13px", mt: "4px" },
            "& .MuiStepIcon-root.Mui-active": { color: theme.primary },
            "& .MuiStepIcon-root.Mui-completed": { color: theme.primary },
            "& .MuiStepConnector-line": { borderColor: "rgba(0,0,0,0.1)" },
          }}
        >
          {steps.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box component="form" id="event-form">
          {/* Step 0 — Basic Info */}
          {activeStep === 0 && (
            <Stack spacing={2}>
              <Box style={sectionLabel}>Event details</Box>
              <TextField
                label="Title *"
                name="title"
                size="small"
                fullWidth
                value={form.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                placeholder="e.g. Monday Live Class"
                sx={fieldSx}
              />
              <FormControl
                size="small"
                fullWidth
                error={!!errors.eventType}
                sx={fieldSx}
              >
                <InputLabel>Event type *</InputLabel>
                <Select
                  name="eventType"
                  value={form.eventType}
                  onChange={handleChange}
                  label="Event type *"
                  sx={{
                    borderRadius: theme.radius,
                    fontSize: "13px",
                    textTransform: "capitalize",
                  }}
                >
                  {eventTypes?.length > 0 ? (
                    eventTypes.map((eventType) => (
                      <MenuItem
                        sx={{ textTransform: "capitalize" }}
                        key={eventType.eventTypeId}
                        value={eventType.eventTypeId}
                      >
                        {eventType.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No event types available</MenuItem>
                  )}
                </Select>
                <FormHelperText>{errors.eventType}</FormHelperText>
              </FormControl>
            </Stack>
          )}

          {/* Step 1 — Recurrence */}
          {activeStep === 1 && (
            <Stack spacing={2}>
              <Box style={sectionLabel}>Recurrence</Box>
              <Box style={toggleBox}>
                <Checkbox
                  name="recurrence"
                  checked={form.recurrence}
                  onChange={handleChange}
                  size="small"
                  sx={{
                    p: 0,
                    color: theme.primary,
                    "&.Mui-checked": { color: theme.primary },
                  }}
                />
                <Box sx={{ fontSize: "13px", fontWeight: 500 }}>
                  Repeat this event
                </Box>
              </Box>

              {form.recurrence && (
                <>
                  {/* Frequency */}
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors.frequency}
                    sx={fieldSx}
                  >
                    <InputLabel>Frequency *</InputLabel>
                    <Select
                      name="frequency"
                      value={form.frequency}
                      onChange={handleChange}
                      label="Frequency *"
                      sx={{ borderRadius: theme.radius, fontSize: "13px" }}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                    <FormHelperText>{errors.frequency}</FormHelperText>
                  </FormControl>

                  {/* Days of week — weekly only */}
                  {form.frequency === "weekly" && (
                    <MultiSelect
                      label="Days of week *"
                      name="daysOfWeek"
                      value={form.daysOfWeek}
                      onChange={handleChange}
                      options={weekDays.map((d) => ({ label: d, value: d }))}
                      error={errors.daysOfWeek}
                    />
                  )}

                  {/* Recurrence duration type */}
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors.recurrenceType}
                    sx={fieldSx}
                  >
                    <InputLabel>Recurrence duration *</InputLabel>
                    <Select
                      name="recurrenceType"
                      value={form.recurrenceType}
                      onChange={handleChange}
                      label="Recurrence duration *"
                      sx={{ borderRadius: theme.radius, fontSize: "13px" }}
                    >
                      <MenuItem value="infinite">
                        Infinite (no end date)
                      </MenuItem>
                      <MenuItem value="finite">Until a specific date</MenuItem>
                    </Select>
                    <FormHelperText>{errors.recurrenceType}</FormHelperText>
                  </FormControl>

                  {/* Recurrence end date — only when "finite" is selected */}
                  {form.recurrenceType === "finite" && (
                    <TextField
                      label="Recurrence end date *"
                      name="recurrenceEndDate"
                      type="date"
                      size="small"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={form.recurrenceEndDate}
                      onChange={handleChange}
                      error={!!errors.recurrenceEndDate}
                      helperText={errors.recurrenceEndDate}
                      inputProps={{ min: getTodayStr() }}
                      sx={fieldSx}
                    />
                  )}
                </>
              )}

              {!form.recurrence && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 2,
                    fontSize: "13px",
                    color: "text.disabled",
                  }}
                >
                  No recurrence — event runs once
                </Box>
              )}
            </Stack>
          )}

          {/* Step 2 — Date & Time (unchanged) */}
          {activeStep === 2 && (
            <Stack spacing={2}>
              <Box style={sectionLabel}>Schedule</Box>
              <Box style={toggleBox}>
                <Checkbox
                  name="allDay"
                  checked={form.allDay}
                  onChange={handleChange}
                  size="small"
                  sx={{
                    p: 0,
                    color: theme.primary,
                    "&.Mui-checked": { color: theme.primary },
                  }}
                />
                <Box sx={{ fontSize: "13px", fontWeight: 500 }}>
                  All day event
                </Box>
              </Box>
              <TextField
                label="Start date *"
                name="startDate"
                type="date"
                size="small"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.startDate}
                onChange={handleChange}
                error={!!errors.startDate}
                helperText={errors.startDate}
                sx={fieldSx}
              />
              {!form.recurrence && !form.allDay && (
                <TextField
                  label="End date *"
                  name="endDate"
                  type="date"
                  size="small"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={form.endDate}
                  onChange={handleChange}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  inputProps={{ min: form.startDate }}
                  sx={fieldSx}
                />
              )}
              {!form.allDay && (
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Start time *"
                    name="startTime"
                    type="time"
                    size="small"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={form.startTime}
                    onChange={handleChange}
                    error={!!errors.startTime}
                    helperText={errors.startTime}
                    sx={fieldSx}
                  />
                  <TextField
                    label="End time *"
                    name="endTime"
                    type="time"
                    size="small"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={form.endTime}
                    onChange={handleChange}
                    error={!!errors.endTime}
                    helperText={errors.endTime}
                    sx={fieldSx}
                  />
                </Stack>
              )}
            </Stack>
          )}

          {/* Step 3 — Visibility (unchanged) */}
          {activeStep === 3 && (
            <Stack spacing={2}>
              <Box style={sectionLabel}>Visibility</Box>
              <FormControl
                size="small"
                fullWidth
                error={!!errors.visibility}
                sx={fieldSx}
              >
                <InputLabel>Who can see this? *</InputLabel>
                <Select
                  name="visibility"
                  value={form.visibility}
                  onChange={handleChange}
                  label="Who can see this? *"
                  sx={{ borderRadius: theme.radius, fontSize: "13px" }}
                >
                  <MenuItem value="organization">Organization</MenuItem>
                  <MenuItem value="user">Specific users</MenuItem>
                  <MenuItem value="academicYear">Academic year</MenuItem>
                  <MenuItem value="classSection">Class section</MenuItem>
                </Select>
                <FormHelperText>{errors.visibility}</FormHelperText>
              </FormControl>
              {form.visibility === "user" && (
                <MultiSelect
                  label="Participants *"
                  name="participants"
                  value={form.participants}
                  onChange={handleChange}
                  options={
                    users?.map((u) => ({ label: u.name, value: u.userId })) ||
                    []
                  }
                  error={errors.participants}
                />
              )}
              {form.visibility === "academicYear" && (
                <MultiSelect
                  label="Academic Year *"
                  name="academicYear"
                  value={form.academicYear}
                  onChange={handleChange}
                  options={
                    academicYears?.academicYearId
                      ? [
                          {
                            label: academicYears.name,
                            value: academicYears.academicYearId,
                          },
                        ]
                      : []
                  }
                  error={errors.academicYear}
                />
              )}
              {form.visibility === "classSection" && (
                <MultiSelect
                  label="Sections *"
                  name="sections"
                  value={form.sections}
                  onChange={handleChange}
                  options={
                    classSections?.map((s) => ({
                      label: s.title,
                      value: s.classSectionId,
                    })) || []
                  }
                  error={errors.sections}
                />
              )}
            </Stack>
          )}

          {/* Step 4 — Message (unchanged) */}
          {activeStep === 4 && (
            <Box>
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "text.secondary",
                  mb: 0.5,
                }}
              >
                Message *
              </Box>
              <RichTextEditor
                initialContent={form.message}
                onChange={(val) => {
                  const updatedForm = { ...form, message: val };
                  setForm(updatedForm);
                  setErrors(validateStep(activeStep, updatedForm));
                }}
              />
              {errors.message && (
                <FormHelperText error sx={{ mt: 0.5, ml: 0 }}>
                  {errors.message}
                </FormHelperText>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid rgba(0,0,0,0.07)",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            textTransform: "none",
            borderRadius: theme.radius,
            fontSize: "15px",
            border: "0.5px solid rgba(0,0,0,0.15)",
            color: "text.primary",
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ fontSize: "12px", color: "text.disabled", mr: 0.5 }}>
            Step {activeStep + 1} of {steps.length}
          </Box>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              sx={{
                textTransform: "none",
                borderRadius: theme.radius,
                fontSize: "15px",
                border: "0.5px solid rgba(0,0,0,0.15)",
                color: "text.primary",
                px: 2,
              }}
            >
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: theme.radius,
                fontSize: "15px",
                px: 2.5,
                background: theme.primary,
                boxShadow: "none",
                "&:hover": { background: "#1a3a8a", boxShadow: "none" },
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              type="button"
              form="event-form"
              variant="contained"
              loading={formLoading}
              sx={{
                textTransform: "none",
                borderRadius: theme.radius,
                fontSize: "13px",
                px: 2.5,
                background: theme.primary,
                boxShadow: "none",
                "&:hover": { background: "#1a3a8a", boxShadow: "none" },
              }}
            >
              Submit
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EventsForm;
